// src/admin/CategoryManagement.js
import React, { useState, useEffect } from 'react';
import {
  collection, getDocs, doc, setDoc, updateDoc, deleteDoc
} from 'firebase/firestore';
import { db } from '../firebase';
import { uploadToImageKit } from '../utils/imagekitUpload';
import {
  Plus, Edit, Trash2, X, Upload, FolderTree, Search, Package
} from 'lucide-react';

const CategoryManagement = () => {
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [searchTerm, setSearchTerm] = useState('');

  const defaultFormData = {
    name: '',
    image: '',
    description: '',
  };

  const [formData, setFormData] = useState(defaultFormData);

  // ================= FETCH DATA =================
  const fetchData = async () => {
    setLoading(true);
    try {
      const [catSnap, prodSnap] = await Promise.all([
        getDocs(collection(db, 'categories')),
        getDocs(collection(db, 'products')),
      ]);

      const catData = catSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      const prodData = prodSnap.docs.map(d => ({ id: d.id, ...d.data() }));

      setCategories(catData);
      setProducts(prodData);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // ================= GET PRODUCT COUNT =================
  const getProductCount = (categoryName) => {
    return products.filter(p => p.category === categoryName).length;
  };

  // ================= IMAGE UPLOAD =================
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    setMessage({ type: '', text: '' });

    try {
      const url = await uploadToImageKit(file, 'categories');
      setFormData({ ...formData, image: url });
      setMessage({ type: 'success', text: 'Image uploaded!' });
    } catch (error) {
      console.error('Upload error:', error);
      setMessage({ type: 'error', text: 'Upload failed.' });
    } finally {
      setUploading(false);
    }
  };

  // ================= SAVE CATEGORY =================
  const handleSave = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    if (!formData.name || !formData.image) {
      setMessage({ type: 'error', text: 'Name aur Image zaroori hai.' });
      return;
    }

    const categoryData = {
      name: formData.name,
      image: formData.image,
      description: formData.description || '',
      productCount: getProductCount(formData.name),
    };

    try {
      if (editingCategory) {
        // Check agar name change hua hai
        const oldName = editingCategory.name;
        const newName = formData.name;

        await updateDoc(doc(db, 'categories', editingCategory.id), categoryData);

        // Agar name change hua, toh products ki category bhi update karein
        if (oldName !== newName) {
          const productsToUpdate = products.filter(p => p.category === oldName);
          for (const prod of productsToUpdate) {
            await updateDoc(doc(db, 'products', prod.id), { category: newName });
          }
        }

        setMessage({ type: 'success', text: 'Category updated!' });
      } else {
        const newId = Date.now().toString();
        await setDoc(doc(db, 'categories', newId), {
          ...categoryData,
          createdAt: new Date(),
        });
        setMessage({ type: 'success', text: 'Category created!' });
      }

      setTimeout(() => {
        setShowModal(false);
        setEditingCategory(null);
        setFormData(defaultFormData);
        fetchData();
      }, 1000);

    } catch (error) {
      console.error('Save error:', error);
      setMessage({ type: 'error', text: 'Failed to save category.' });
    }
  };

  // ================= DELETE =================
  const handleDelete = async (category) => {
    const productCount = getProductCount(category.name);

    if (productCount > 0) {
      if (!window.confirm(
        `⚠️ Category "${category.name}" mein ${productCount} products hain.\n\n` +
        `Pehle un products ko doosri category mein move karein ya delete karein.\n\n` +
        `Phir ye category delete kar sakte hain.`
      )) return;
      return;
    }

    if (!window.confirm(`Delete category "${category.name}"?`)) return;

    try {
      await deleteDoc(doc(db, 'categories', category.id));
      fetchData();
    } catch (error) {
      console.error('Delete error:', error);
      alert('Failed to delete category.');
    }
  };

  // ================= MODAL =================
  const openAddModal = () => {
    setEditingCategory(null);
    setFormData(defaultFormData);
    setShowModal(true);
    setMessage({ type: '', text: '' });
  };

  const openEditModal = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name || '',
      image: category.image || '',
      description: category.description || '',
    });
    setShowModal(true);
    setMessage({ type: '', text: '' });
  };

  // ================= FILTER =================
  const filteredCategories = categories.filter(c =>
    c.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Categories Management</h1>
          <p className="text-gray-500 text-sm mt-1">
            {categories.length} categories • Product count automatically update hota hai.
          </p>
        </div>
        <button
          onClick={openAddModal}
          className="bg-green-700 text-white px-5 py-3 rounded-lg hover:bg-green-800 transition flex items-center gap-2 font-semibold shadow-md"
        >
          <Plus size={18} /> Add Category
        </button>
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl shadow-md p-4">
        <div className="relative">
          <input
            type="text"
            placeholder="Search categories..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full border border-gray-300 rounded-lg py-3 px-4 pl-11 focus:outline-none focus:border-green-600 text-sm"
          />
          <Search className="absolute left-3 top-3.5 text-gray-400" size={18} />
        </div>
      </div>

      {/* Categories Grid */}
      {loading ? (
        <div className="text-center py-16">
          <div className="inline-block animate-spin rounded-full h-10 w-10 border-4 border-green-700 border-t-transparent"></div>
          <p className="mt-4 text-gray-500">Loading categories...</p>
        </div>
      ) : filteredCategories.length === 0 ? (
        <div className="bg-white rounded-xl shadow-md p-12 text-center">
          <FolderTree size={48} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-500">No categories yet. Add your first category!</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredCategories.map((category) => {
            const count = getProductCount(category.name);
            return (
              <div
                key={category.id}
                className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition flex items-center gap-4 p-4"
              >
                {/* Image */}
                <div className="w-20 h-20 rounded-full overflow-hidden bg-gray-100 shrink-0 ring-2 ring-green-100">
                  <img
                    src={category.image}
                    alt={category.name}
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-gray-800 text-lg truncate">{category.name}</h3>
                  {category.description && (
                    <p className="text-xs text-gray-500 line-clamp-1 mt-0.5">
                      {category.description}
                    </p>
                  )}
                  <div className="flex items-center gap-1 mt-1">
                    <Package size={12} className="text-green-700" />
                    <span className="text-xs font-semibold text-green-700">
                      {count} {count === 1 ? 'product' : 'products'}
                    </span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-col gap-2">
                  <button
                    onClick={() => openEditModal(category)}
                    className="bg-blue-50 hover:bg-blue-100 text-blue-700 p-2 rounded-lg transition"
                    title="Edit"
                  >
                    <Edit size={16} />
                  </button>
                  <button
                    onClick={() => handleDelete(category)}
                    className="bg-red-50 hover:bg-red-100 text-red-700 p-2 rounded-lg transition"
                    title="Delete"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ================= MODAL ================= */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">

            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <h2 className="text-lg font-bold text-gray-800">
                {editingCategory ? 'Edit Category' : 'Add New Category'}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-gray-500 hover:bg-gray-100 p-2 rounded-lg">
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSave} className="p-5 space-y-4">

              {message.text && (
                <div className={`text-sm p-3 rounded-lg ${
                  message.type === 'success'
                    ? 'bg-green-50 text-green-700 border border-green-200'
                    : 'bg-red-50 text-red-700 border border-red-200'
                }`}>
                  {message.text}
                </div>
              )}

              {/* Image */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Category Image *</label>
                {formData.image ? (
                  <div className="relative flex justify-center">
                    <img
                      src={formData.image}
                      alt="Category preview"
                      className="w-32 h-32 object-cover rounded-full ring-4 ring-green-100"
                    />
                    <label className="absolute bottom-0 right-1/2 translate-x-16 bg-white text-gray-700 px-3 py-1 rounded-lg shadow-md text-xs font-semibold cursor-pointer hover:bg-gray-50">
                      Change
                      <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
                    </label>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-green-500 hover:bg-green-50 transition">
                    {uploading ? (
                      <>
                        <div className="animate-spin rounded-full h-8 w-8 border-4 border-green-700 border-t-transparent"></div>
                        <p className="mt-2 text-sm text-gray-600">Uploading...</p>
                      </>
                    ) : (
                      <>
                        <Upload className="text-gray-400 mb-2" size={28} />
                        <p className="text-sm font-semibold text-gray-600">Click to upload image</p>
                        <p className="text-xs text-gray-400 mt-1">Square image recommended</p>
                      </>
                    )}
                    <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" disabled={uploading} />
                  </label>
                )}
              </div>

              {/* Name */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Category Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm"
                  placeholder="e.g., Fruits, Vegetables"
                  required
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Description (Optional)</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={2}
                  className="w-full border border-gray-300 rounded-lg py-3 px-4 focus:outline-none focus:border-green-600 text-sm resize-none"
                  placeholder="Short description..."
                />
              </div>

              {/* Submit */}
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 border border-gray-300 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={uploading}
                  className={`flex-1 bg-green-700 text-white py-3 rounded-lg font-semibold hover:bg-green-800 transition flex items-center justify-center gap-2 ${
                    uploading ? 'opacity-70 cursor-not-allowed' : ''
                  }`}
                >
                  <FolderTree size={18} />
                  {editingCategory ? 'Update' : 'Create'}
                </button>
              </div>

            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default CategoryManagement;